// src/components/DecisionTree.js

import React, { useState, useEffect } from "react";
import { ChevronsRightLeft, ChevronLeft, RotateCcw } from "lucide-react";
import { InfoTooltip } from './UIComponents';

const TreeNode = ({ node, depth = 0, isLast, currentNodeId, onNodeClick, expandedNodes, toggleExpanded, onShowTooltip, onHideTooltip, activePathNodeIds, completedPaths }) => {
    const isCurrentNode = node.id === currentNodeId;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isOnActivePath = activePathNodeIds.has(node.id);
    const isComplete = node.isComplete;
    const isEntryConnectorActive = isOnActivePath && activePathNodeIds.has(node.parent);
    const activeChildIndex = node.children.findIndex(child => activePathNodeIds.has(child.id));
    let pathNumber = null;
    if (node.isComplete && completedPaths) {
      const pathIndex = completedPaths.findIndex(path => path.nodes[path.nodes.length - 1].id === node.id);
      if (pathIndex !== -1) { pathNumber = pathIndex + 1; }
    }
    const dotColor = isCurrentNode ? '#2563eb' : isComplete ? '#2563eb' : isOnActivePath ? '#16a34a' : '#9ca3af';
    return (
      <div className="relative">
        {depth > 0 && (
          <>
            <div className={`absolute top-5 h-px ${isEntryConnectorActive ? 'bg-green-500' : 'bg-gray-200'}`} style={{left: `${(depth - 1) * 24 + 12}px`, width: '12px', zIndex: isEntryConnectorActive ? 2 : 1}}/>
            <div className={`absolute top-0 w-px ${isEntryConnectorActive ? 'bg-green-500' : 'bg-gray-200'}`} style={{ left: `${(depth - 1) * 24 + 12}px`, height: '22px', zIndex: isEntryConnectorActive ? 2 : 1}}/>
            {!isLast && (<div className={`absolute w-px bg-gray-200`} style={{ left: `${(depth - 1) * 24 + 12}px`, top: '22px', height: 'calc(100% - 22px)', zIndex: 1}}/>)}
          </>
        )}
        <div className={`group flex items-start p-1 pr-2 hover:bg-gray-100 cursor-pointer rounded relative ${isCurrentNode ? 'bg-blue-100' : ''}`} style={{ paddingLeft: `${depth * 24 + 8}px` }} onClick={() => onNodeClick(node.id)} onMouseEnter={(e) => onShowTooltip(e, node)} onMouseLeave={onHideTooltip}>
          {hasChildren ? (<span className="mr-1 text-gray-400 text-xs w-4 text-center cursor-pointer flex-shrink-0 z-10 pt-1" onClick={(e) => {e.stopPropagation(); toggleExpanded(node.id);}}>{isExpanded ? '▼' : '▶'}</span> ) : <span className="w-4 mr-1 flex-shrink-0"></span>}
          <div className={`flex items-start flex-1 ml-1 ${isCurrentNode ? 'border-l-4 border-blue-600 -ml-1 pl-2' : ''}`}>
            <div className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 mt-1.5`} style={{ backgroundColor: dotColor, zIndex: 3 }}/>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-800 whitespace-normal break-words">{node.turn > 0 ? node.decision.text : "Scenario Start"}</div>
              {pathNumber !== null && (<span className="mt-1 inline-block px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">Path #{pathNumber}</span>)}
            </div>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child, index) => {
                const isTrunkForSiblingActive = activeChildIndex !== -1 && index < activeChildIndex && activePathNodeIds.has(node.id);
                return(
                    <div key={child.id} className="relative">
                        {isTrunkForSiblingActive && (<div className="absolute w-px bg-green-500" style={{left: `${(depth) * 24 + 12}px`, top: 0, height: '100%', zIndex: 2}}/>)}
                        <TreeNode node={child} depth={depth + 1} isLast={index === node.children.length - 1} currentNodeId={currentNodeId} onNodeClick={onNodeClick} expandedNodes={expandedNodes} toggleExpanded={toggleExpanded} onShowTooltip={onShowTooltip} onHideTooltip={onHideTooltip} activePathNodeIds={activePathNodeIds} completedPaths={completedPaths}/>
                    </div>
                );
            })}
          </div>
        )}
      </div>
    );
};

export const DecisionTreePanel = ({ decisionTree, currentNodeId, onNodeClick, findNodeById, onRestart, onShowTooltip, onHideTooltip, activePathNodeIds, onToggleExpand, isExpanded, completedPaths }) => {
    const [expandedNodes, setExpandedNodes] = useState(new Set(['root']));
    useEffect(() => {
        if (!currentNodeId || !decisionTree) return;
        const ancestorIds = new Set(['root']);
        let currentNode = findNodeById(decisionTree, currentNodeId);
        while (currentNode && currentNode.parent) {
            ancestorIds.add(currentNode.parent);
            currentNode = findNodeById(decisionTree, currentNode.parent);
        }
        setExpandedNodes(prev => new Set([...Array.from(prev), ...Array.from(ancestorIds)]));
    }, [currentNodeId, decisionTree, findNodeById]);

    const toggleExpanded = (nodeId) => {
      setExpandedNodes(prev => {
        const newExpanded = new Set(prev);
        if (newExpanded.has(nodeId)) { newExpanded.delete(nodeId); } else { newExpanded.add(nodeId); }
        return newExpanded;
      });
    };
  
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 h-full flex flex-col">
        <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900">Decision Path Explorer</h3>
                <InfoTooltip>This explorer visualizes every decision path.<br/><br/><strong className="font-semibold">Click any node</strong> to "time-travel" and view the metrics at that point in the simulation.</InfoTooltip>
            </div>
            <div>
              <button onClick={onToggleExpand} className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors mr-2" title={isExpanded ? "Collapse Panel" : "Expand Panel"}>{isExpanded ? <ChevronLeft className="w-4 h-4" /> : <ChevronsRightLeft className="w-4 h-4" />}</button>
              <button onClick={onRestart} className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors" title="Restart Scenario"><RotateCcw className="w-4 h-4" /></button>
            </div>
        </div>

        {/* FIX: Moved the key from the bottom to here, below the header. */}
        <div className="pb-4 mb-4 border-b border-gray-200 space-y-2">
          <div className="flex items-center gap-2 text-sm"><div className="w-2 h-2 bg-blue-600 rounded-full"></div><span>Current / Complete</span></div>
          <div className="flex items-center gap-2 text-sm"><div className="w-2 h-2 bg-green-500 rounded-full"></div><span>Selected Path</span></div>
          <div className="flex items-center gap-2 text-sm"><div className="w-2 h-2 bg-gray-400 rounded-full"></div><span>Unexplored Decision</span></div>
        </div>

        {/* FIX: The tree container now correctly fills the remaining space. */}
        <div className="flex-1 space-y-1 overflow-y-auto">
          {decisionTree && (
            <TreeNode node={decisionTree} depth={0} isLast={true} currentNodeId={currentNodeId} onNodeClick={onNodeClick} expandedNodes={expandedNodes} toggleExpanded={toggleExpanded} onShowTooltip={onShowTooltip} onHideTooltip={onHideTooltip} activePathNodeIds={activePathNodeIds} completedPaths={completedPaths} />
          )}
        </div>
      </div>
    );
};
